import React from "react";
import {Card, CardBody, CardHeader} from 'reactstrap';
import '../../../i18/config';
import {useTranslation} from "react-i18next";

import {StatPeerActivityReactionsType} from "../../../redux/Stat/PeerActivity/types";
import {PostFigureState} from "./postFigureState";

interface InputProps {
    // {TYPE: count}; null when the network does not provide the breakdown.
    reactions: StatPeerActivityReactionsType | null | undefined;
    // What to say when there is no breakdown (see postFigureState).
    emptyState: PostFigureState;
    isLoading?: boolean;
}

// Network reaction types, most common first (LinkedIn names).
const REACTION_ORDER = ['LIKE', 'PRAISE', 'EMPATHY', 'INTEREST', 'APPRECIATION', 'ENTERTAINMENT'];

const sortedReactions = (reactions: StatPeerActivityReactionsType): Array<[string, number]> =>
    Object.entries(reactions)
        .filter(([, count]) => typeof count === 'number')
        .sort(([a, countA], [b, countB]) => {
            const orderA = REACTION_ORDER.indexOf(a);
            const orderB = REACTION_ORDER.indexOf(b);
            if (orderA !== orderB) {
                return (orderA === -1 ? REACTION_ORDER.length : orderA) - (orderB === -1 ? REACTION_ORDER.length : orderB);
            }
            return countB - countA;
        });

const PostReactionsCard = ({reactions, emptyState, isLoading = false}: InputProps) => {

    const {t} = useTranslation(['stat']);

    const items = reactions ? sortedReactions(reactions) : [];
    const total = items.reduce((sum, [, count]) => sum + count, 0);

    return (
        <Card>
            <CardHeader className="d-flex align-items-center">
                <h4 className="card-title flex-grow-1 mb-0">{t('stat:reactions')}</h4>
                {reactions && <span className="badge bg-light text-body fs-12">{total}</span>}
            </CardHeader>
            <CardBody>
                {isLoading ?
                    <p className="placeholder-glow mb-0"><span className="placeholder col-12 rounded-2"/></p>
                    : reactions === null || reactions === undefined ?
                        <p className="text-muted mb-0" data-testid="post-reactions-empty">
                            {emptyState === 'notYetAvailable' ? t('stat:notYetAvailable') : t('stat:unavailable')}
                        </p>
                        : items.length === 0 ?
                            <p className="text-muted mb-0" data-testid="post-reactions-none">{t('stat:noReactions')}</p>
                            :
                            <ul className="list-unstyled mb-0 vstack gap-3" data-testid="post-reactions-list">
                                {items.map(([type, count]) => (
                                    <li key={'reaction_' + type}>
                                        <div className="d-flex align-items-center mb-1">
                                            <span className="flex-grow-1 text-truncate">{t('stat:reaction_' + type, {defaultValue: type})}</span>
                                            <span className="fw-semibold ms-2">{count}</span>
                                        </div>
                                        <div className="progress progress-sm">
                                            <div className="progress-bar bg-primary" role="progressbar"
                                                 style={{width: (total > 0 ? Math.round(count / total * 100) : 0) + '%'}}
                                                 aria-valuenow={count} aria-valuemin={0} aria-valuemax={total}/>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                }
            </CardBody>
        </Card>
    );
};

export default PostReactionsCard;
